/**
 * Typed wrapper over the wasm-bindgen output in `pkg/`. Call `initCore()` once before anything else.
 */
import type {
  CheckoutStatus,
  EncryptedOp,
  Hlc,
  HoldResolution,
  KdfParams,
  Op,
  Rule,
  StoredOp,
  WrappedVaultKey,
} from "@envstash/protocol";
import initWasm, * as wasm from "../pkg/envstash_core_wasm.js";
import type { ApplyResult, ParsedEnv, Snapshot } from "./types.js";

export * from "./types.js";

let ready: Promise<void> | null = null;

/** Initialize the WASM module. In the browser the `.wasm` URL is resolved relative to the JS. */
export function initCore(input?: wasm.InitInput): Promise<void> {
  ready ??= initWasm(input === undefined ? undefined : { module_or_path: input }).then(() => undefined);
  return ready;
}

const bytes = (v: unknown): Uint8Array => (v instanceof Uint8Array ? v : Uint8Array.from(v as number[]));

export const generateVaultKey = (): Uint8Array => wasm.generateVaultKey();
export const randomBytes = (n: number): Uint8Array => wasm.randomBytes(n);
export const newKdfParams = (): KdfParams => wasm.newKdfParams() as KdfParams;

export function derivePasswordKeys(password: string, kdf: KdfParams): { wrapKey: Uint8Array; authKey: Uint8Array } {
  const r = wasm.derivePasswordKeys(password, kdf) as { wrap_key: unknown; auth_key: unknown };
  return { wrapKey: bytes(r.wrap_key), authKey: bytes(r.auth_key) };
}

export const wrapPassword = (vk: Uint8Array, wrapKey: Uint8Array, kdf: KdfParams): WrappedVaultKey =>
  wasm.wrapPassword(vk, wrapKey, kdf) as WrappedVaultKey;
export const unwrapPassword = (wrap: WrappedVaultKey, wrapKey: Uint8Array): Uint8Array =>
  wasm.unwrapPassword(wrap, wrapKey);

export const generateRecoveryKey = (): string => wasm.generateRecoveryKey();
export const normalizeRecoveryKey = (input: string): string => wasm.normalizeRecoveryKey(input);
export const recoveryAuthKey = (input: string): Uint8Array => wasm.recoveryAuthKey(input);
export const wrapRecovery = (vk: Uint8Array, recovery: string): WrappedVaultKey =>
  wasm.wrapRecovery(vk, recovery) as WrappedVaultKey;
export const unwrapRecovery = (wrap: WrappedVaultKey, recovery: string): Uint8Array =>
  wasm.unwrapRecovery(wrap, recovery);

export const wrapDevice = (vk: Uint8Array, deviceId: string, devicePublicKeyB64: string): WrappedVaultKey =>
  wasm.wrapDevice(vk, deviceId, devicePublicKeyB64) as WrappedVaultKey;
export const unwrapDeviceWithShared = (wrap: WrappedVaultKey, shared: Uint8Array, devicePk: Uint8Array): Uint8Array =>
  wasm.unwrapDeviceWithShared(wrap, shared, devicePk);
export const unwrapDeviceWithSecret = (wrap: WrappedVaultKey, secret: Uint8Array): Uint8Array =>
  wasm.unwrapDeviceWithSecret(wrap, secret);
export function generateSoftwareDeviceKey(): { secret: Uint8Array; public: Uint8Array } {
  const r = wasm.generateSoftwareDeviceKey() as { secret: unknown; public: unknown };
  return { secret: bytes(r.secret), public: bytes(r.public) };
}

export const wrapPasskey = (
  vk: Uint8Array,
  credentialId: string,
  prfSalt: Uint8Array,
  prfOutput: Uint8Array,
): WrappedVaultKey => wasm.wrapPasskey(vk, credentialId, prfSalt, prfOutput) as WrappedVaultKey;
export const unwrapPasskey = (wrap: WrappedVaultKey, prfOutput: Uint8Array): Uint8Array =>
  wasm.unwrapPasskey(wrap, prfOutput);

export const contentHash = (data: Uint8Array): string => wasm.contentHash(data);
/** Short fingerprint of a device public key, shown on both ends of an enrollment. */
export const deviceFingerprint = (publicKeyB64: string): string => wasm.deviceFingerprint(publicKeyB64);
export const isValidEnvKey = (key: string): boolean => wasm.isValidEnvKey(key);
export function encryptBlob(plaintext: Uint8Array): { key: Uint8Array; ciphertext: Uint8Array; ref: string } {
  const r = wasm.encryptBlob(plaintext) as { key: unknown; ciphertext: unknown; ref: string };
  return { key: bytes(r.key), ciphertext: bytes(r.ciphertext), ref: r.ref };
}
export const decryptBlob = (key: Uint8Array, ciphertext: Uint8Array): Uint8Array => wasm.decryptBlob(key, ciphertext);
export const parseEnv = (src: string): ParsedEnv => wasm.parseEnv(src) as ParsedEnv;
export const defaultRules = (): Rule[] => wasm.Vault.defaultRules() as Rule[];
/** Deterministic id for a default rule, so devices seeding defaults concurrently converge. */
export const defaultRuleId = (rule: Rule): string => wasm.Vault.defaultRuleId(rule);

/** An unlocked vault: vault key + materialized state + this device's HLC clock. */
export class Vault {
  readonly #inner: wasm.Vault;

  constructor(vk: Uint8Array, vaultId: string, deviceId: string) {
    this.#inner = new wasm.Vault(vk, vaultId, deviceId);
  }

  vaultKey(): Uint8Array {
    return this.#inner.vaultKey();
  }
  lastSeq(): number {
    return this.#inner.lastSeq();
  }
  applyStored(ops: StoredOp[], nowMs = Date.now()): ApplyResult {
    return this.#inner.applyStored(ops, nowMs) as ApplyResult;
  }
  decryptOp(op: StoredOp): Op {
    return this.#inner.decryptOp(op) as Op;
  }
  /** Next HLC for an op authored by this device. */
  nextHlc(nowMs = Date.now()): Hlc {
    return this.#inner.nextHlc(nowMs) as Hlc;
  }
  /** Encrypts and applies a locally-authored op; returns what to push. */
  commit(op: Op): { encrypted: EncryptedOp; op: Op } {
    return this.#inner.commit(op) as { encrypted: EncryptedOp; op: Op };
  }
  snapshot(): Snapshot {
    return this.#inner.snapshot() as Snapshot;
  }
  renderEnv(fileId: string): string | null {
    return this.#inner.renderEnv(fileId) ?? null;
  }
  envFileOps(fileId: string, content: string, nowMs = Date.now()): Op[] {
    return this.#inner.envFileOps(fileId, content, nowMs) as Op[];
  }
  setKeyOp(fileId: string, key: string, value: string | null, nowMs = Date.now()): Op {
    return this.#inner.setKeyOp(fileId, key, value, nowMs) as Op;
  }
  ruleUpsertOp(ruleId: string | null, rule: Rule, nowMs = Date.now()): Op {
    return this.#inner.ruleUpsertOp(ruleId, rule, nowMs) as Op;
  }
  ruleDeleteOp(ruleId: string, nowMs = Date.now()): Op {
    return this.#inner.ruleDeleteOp(ruleId, nowMs) as Op;
  }
  untrackOp(fileId: string, nowMs = Date.now()): Op {
    return this.#inner.untrackOp(fileId, nowMs) as Op;
  }
  blobContent(
    fileId: string,
    contentHash: string,
  ): { kind: "inline"; data: string } | { kind: "r2"; ref: string; key: string } | null {
    return (this.#inner.blobContent(fileId, contentHash) ?? null) as
      { kind: "inline"; data: string } | { kind: "r2"; ref: string; key: string } | null;
  }
  checkoutOp(projectId: string, deviceId: string, localPath: string, status: CheckoutStatus, nowMs = Date.now()): Op {
    return this.#inner.checkoutOp(projectId, deviceId, localPath, status, nowMs) as Op;
  }
  /** Ops that resolve a hold; for `use_local` on an env file pass the snapshot's text. */
  resolveHoldOps(
    fileId: string,
    deviceId: string,
    localPath: string,
    contentHash: string,
    resolution: HoldResolution,
    text: string | null,
    nowMs = Date.now(),
  ): Op[] {
    return this.#inner.resolveHoldOps(
      fileId,
      deviceId,
      localPath,
      contentHash,
      resolution,
      text ?? undefined,
      nowMs,
    ) as Op[];
  }
  /** A new blob version with the content of an earlier one, or null when it is already current. */
  blobRevertOp(fileId: string, contentHash: string, nowMs = Date.now()): Op | null {
    return (this.#inner.blobRevertOp(fileId, contentHash, nowMs) ?? null) as Op | null;
  }
  free(): void {
    this.#inner.free();
  }
}
